import React from 'react';
import {
  applyTypography,
  DEFAULT_TYPOGRAPHY_ID,
  getTypography,
  getTypographyList,
  isValidTypographyId,
  readStoredTypographyId,
  storeTypographyId,
  type TypographyId,
} from './typography';
import { TypographyContext, type TypographyContextValue } from './TypographyContext';

const sanitizeTypographyId = (id?: string | null): TypographyId => {
  if (id && isValidTypographyId(id)) {
    return id;
  }
  return DEFAULT_TYPOGRAPHY_ID;
};

interface TypographyProviderProps {
  children: React.ReactNode;
  initialTypographyId?: TypographyId;
}

export function TypographyProvider({ children, initialTypographyId }: TypographyProviderProps) {
  const storedTypographyId = React.useMemo(
    () => sanitizeTypographyId(initialTypographyId ?? readStoredTypographyId()),
    [initialTypographyId],
  );
  const [typographyId, setTypographyId] = React.useState<TypographyId>(storedTypographyId);
  const typographyList = React.useMemo(() => getTypographyList(), []);
  const typography = React.useMemo(() => getTypography(typographyId), [typographyId]);

  React.useEffect(() => {
    applyTypography(typographyId);
    storeTypographyId(typographyId);
  }, [typographyId]);

  const setTypography = React.useCallback<TypographyContextValue['setTypography']>((id) => {
    setTypographyId((prev) => {
      if (!isValidTypographyId(id)) {
        return prev;
      }
      if (prev === id) {
        return prev;
      }
      return id;
    });
  }, []);

  const value = React.useMemo<TypographyContextValue>(
    () => ({ typographyId, typography, typographyList, setTypography }),
    [typographyId, typography, typographyList, setTypography],
  );

  return <TypographyContext.Provider value={value}>{children}</TypographyContext.Provider>;
}
